import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getClass, activateClass, cancelClass, updateClass } from '../services/classes';
import { listTeachers } from '../services/users';
import { ClassDetail } from '../types/models';
import { RegistrationButton } from '../components/classes/RegistrationButton';
import AttendeeList from '../components/classes/AttendeeList';
import { useAuth } from '../contexts/AuthContext';

export const ClassDetailPage: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const { currentUser, isAuthenticated } = useAuth();
  const [event, setEvent] = useState<ClassDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [activating, setActivating] = useState(false);
  const [availableTeachers, setAvailableTeachers] = useState<{ userId: string; name: string }[]>([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [savingTeachers, setSavingTeachers] = useState(false);

  const load = useCallback(async () => {
    if (!classId) return;
    try {
      setLoading(true);
      const data = await getClass(classId);
      setEvent(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (event?.status === 'Planned' && isAuthenticated) {
      listTeachers().then(setAvailableTeachers).catch(() => {});
    }
  }, [event?.status, isAuthenticated]);

  if (loading) return <div className="text-center py-20 text-gray-500">Loading…</div>;
  if (error) return <div className="max-w-2xl mx-auto px-4 py-8 text-red-600">{error}</div>;
  if (!event) return null;

  const date = new Date(event.startDateTime);
  const isOrganizer = event.organizerId === currentUser?.userId ||
    currentUser?.roles.includes('Admin');
  const isTeacher = event.teachers.some(t => t.userId === currentUser?.userId);
  const hasTeachers = event.teachers.length > 0;

  const handleActivate = async () => {
    if (!confirm('Activate this class? It will become visible to all users and open for registration.')) return;
    setActivating(true);
    try {
      await activateClass(event!.eventId);
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setActivating(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Cancel this class? All registrants will be notified.')) return;
    setCancelling(true);
    try {
      await cancelClass(event.eventId, 'Organizer cancelled');
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setCancelling(false);
    }
  };

  const handleAddTeacher = async () => {
    if (!selectedTeacherId) return;
    setSavingTeachers(true);
    try {
      const newIds = [...event.teachers.map(t => t.userId), selectedTeacherId];
      await updateClass(event.eventId, { teacherIds: newIds });
      setSelectedTeacherId('');
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSavingTeachers(false);
    }
  };

  const handleRemoveTeacher = async (teacherId: string) => {
    setSavingTeachers(true);
    try {
      const newIds = event.teachers.map(t => t.userId).filter(id => id !== teacherId);
      await updateClass(event.eventId, { teacherIds: newIds });
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSavingTeachers(false);
    }
  };

  const unassignedTeachers = availableTeachers.filter(
    t => !event.teachers.some(assigned => assigned.userId === t.userId)
  );

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <Link to="/classes" className="text-sm text-green-600 hover:text-green-700 mb-4 inline-block">
        ← Back to Classes
      </Link>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900">{event.title}</h1>
          <span className="text-sm px-2 py-1 bg-green-100 text-green-700 rounded-full whitespace-nowrap">
            {event.classType}
          </span>
        </div>

        <div className="mt-4 space-y-2 text-sm text-gray-600">
          <div>
            <span className="font-medium text-gray-800">When: </span>
            {date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            {' at '}
            {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            {' · '}
            {event.duration} min
          </div>
          {event.physicalAddress && (
            <div>
              <span className="font-medium text-gray-800">Where: </span>
              {event.physicalAddress.street}, {event.physicalAddress.city}, {event.physicalAddress.state}
            </div>
          )}
          <div>
            <span className="font-medium text-gray-800">Spots: </span>
            {event.registeredCount} / {event.capacity} registered
            {event.waitlistCount > 0 && ` · ${event.waitlistCount} on waitlist`}
          </div>
          {event.teachers.length > 0 && (
            <div>
              <span className="font-medium text-gray-800">Taught by: </span>
              {event.teachers.map((t, i) => (
                <span key={t.userId}>
                  {i > 0 && ', '}
                  <Link to={`/teachers/${t.username}`} className="text-primary-600 hover:underline">
                    {t.name}
                  </Link>
                </span>
              ))}
            </div>
          )}
        </div>

        {event.attendees && (isOrganizer || isTeacher || event.userRegistration?.isRegistered) && (
          <AttendeeList
            attendees={event.attendees}
            waitlist={isOrganizer || isTeacher ? event.waitlist : undefined}
          />
        )}

        <div className="mt-5">
          <p className="text-gray-700 leading-relaxed">
            {event.detailedDescription ?? event.publicDescription}
          </p>
        </div>

        {event.status === 'Planned' && (
          <div className="mt-5 bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-700 text-sm">
            <strong>Draft — not yet published.</strong> This class is only visible to you. Activate it to open registration.
          </div>
        )}

        {isOrganizer && event.status === 'Planned' && (
          <div className="mt-5 pt-5 border-t border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Teachers</h2>

            {event.teachers.length === 0 ? (
              <p className="text-sm text-gray-500 mb-3">No teachers assigned yet. At least one teacher is required to activate.</p>
            ) : (
              <ul className="mb-3 space-y-1">
                {event.teachers.map(teacher => (
                  <li key={teacher.userId} className="flex items-center justify-between text-sm">
                    <span className="text-gray-800">{teacher.name}</span>
                    <button
                      onClick={() => handleRemoveTeacher(teacher.userId)}
                      disabled={savingTeachers}
                      className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {unassignedTeachers.length > 0 && (
              <div className="flex gap-2">
                <select
                  value={selectedTeacherId}
                  onChange={e => setSelectedTeacherId(e.target.value)}
                  className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="">— Select a teacher —</option>
                  {unassignedTeachers.map(t => (
                    <option key={t.userId} value={t.userId}>{t.name}</option>
                  ))}
                </select>
                <button
                  onClick={handleAddTeacher}
                  disabled={!selectedTeacherId || savingTeachers}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            )}
          </div>
        )}

        {event.status === 'Cancelled' && (
          <div className="mt-5 bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">
            This class has been cancelled.
          </div>
        )}

        {isAuthenticated && event.userRegistration && event.status !== 'Cancelled' && event.status !== 'Completed' && event.status !== 'Planned' && (
          <div className="mt-6 pt-5 border-t border-gray-100">
            <RegistrationButton event={event} onUpdate={load} />
          </div>
        )}

        {!isAuthenticated && (
          <div className="mt-6 pt-5 border-t border-gray-100">
            <Link to="/auth" className="text-green-600 hover:underline text-sm">
              Sign in to register for this class →
            </Link>
          </div>
        )}

        {isOrganizer && event.status !== 'Cancelled' && (
          <div className="mt-6 pt-5 border-t border-gray-100 flex flex-wrap gap-3">
            {event.status === 'Planned' && (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleActivate}
                  disabled={activating || !hasTeachers}
                  title={!hasTeachers ? 'Assign at least one teacher before activating' : undefined}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {activating ? 'Activating…' : 'Activate Class'}
                </button>
                {!hasTeachers && (
                  <span className="text-xs text-amber-600">Assign a teacher above first</span>
                )}
              </div>
            )}
            <Link
              to={`/classes/${event.eventId}/edit`}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
            >
              Edit Class
            </Link>
            <button
              onClick={handleCancel}
              disabled={cancelling}
              className="px-4 py-2 border border-red-300 text-red-600 text-sm rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              {cancelling ? 'Cancelling…' : 'Cancel Class'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
